import React, { memo, useContext, useMemo } from 'react';
import DynamicToolbar from '../../../../components/DynamicToolbar';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import AddOffer from '../../AddOffer';
import EditOffer from '../../EditOffer';
import DeleteOffer from '../../DeleteOffer';
import TestOffer from '../../TestOffer';
import useOffer from '../../useOffer';

import { OfferContext } from '../../context';
import ArchiveOffer from '../../ArchiveOffer';
import CloneOffer from '../../CloneOffer';
import BulkEditOffer from '../../EditOffer/bulkEdit';
import ImportIronDome from '../../ImportIronDome';

import FileCopyIcon from '@mui/icons-material/FileCopy';
import SecurityIcon from '@mui/icons-material/Security';
import { showMessage } from '../../../../components/utils/showMessage';

function OfferToolbar({
  selectedRow,
  multipleDataSelected,
  filteredOffers,
  rowToArchive,
  onBulkArchiveDone,
  onAddOfferDone,
  onEditOfferDone,
  onEditBulkOfferDone,
  onDeleteOfferDone,
  onImportOfferDone,
  rowToDelete,
  currentData,
  getTrafficSourceId,
  getRedirectDomainId,
  getEntryDomainId,
  showArchive,
  domainMap,
  trafficMap,
  onCloneOffer,
  setLoading,
  fetchData,
  totalRowCount,
}) {
  const {
    addOffer,
    editOffer,
    editBulkOffer,
    deleteOffer,
    importOffer,
    bulkArchiveOffer,
    bulkStatusOffer,
    singleStatusOffer,
    specificEditOffer,
    getOffers,
    getAllOffersForIronDome,
    cloneOffer,
    bulkEditRules,
    ironDomeBulkImport
  } = useOffer();
  const { traffic, domains } = useContext(OfferContext);

  const onAddOffer = async (newOffer, handleClose, resetValues) => {
    const offer = await addOffer().call(newOffer);
    resetValues({
      offerLink: '',
      trafficSourceId: '',
      name: '',
      fallBackUrl: '',
      redirectDomainId: '',
      entryDomainId: '',
    });
    onAddOfferDone(offer);
    handleClose(true);
  };

  const onEditOffer = async (editField) => {
    const offer = await specificEditOffer().call(selectedRow._id, editField);
    onEditOfferDone(offer);
  };

  const onEditBulkOffer = async (payload) => {
    await editBulkOffer().call(payload);
    onEditBulkOfferDone();
  };

  const onDeleteOffer = async (id, handleClose) => {
    await deleteOffer().call(id);
    onDeleteOfferDone(id);
    handleClose(true);
  };

  const handleBulkArchive = async (initialData, archive) => {
    const offer = await bulkArchiveOffer().call(initialData, archive);
    onBulkArchiveDone(offer.data.updatedOffers);
  };

  const isOfferActive = (offer) => offer.status ?? true;

  const handleBulkStatus = async () => {
  const selectedOfferRecords = currentData.filter((offer) =>
    rowToArchive.includes(offer._id)
  );

  const status = !selectedOfferRecords.every(isOfferActive);

  if (rowToArchive.length === 1) {
    await singleStatusOffer().call(rowToArchive[0], status);
  } else {
    await bulkStatusOffer().call(rowToArchive, status);
  }

  onBulkArchiveDone();
};

const selectedOfferRecords = currentData.filter((offer) =>
  rowToArchive.includes(offer._id)
);

const allSelectedStatus =
  selectedOfferRecords.length > 0 &&
  selectedOfferRecords.every(isOfferActive);

  const handleCloneOffer = async (selectedRow) => {
    try {
      const clonedOffer = await cloneOffer().call(selectedRow);
      onCloneOffer(clonedOffer);
      showMessage({
        message: 'Offer cloned successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error cloning offer:', error);
      showMessage({
        message: 'Failed to clone offer. Please try again.',
        severity: 'error',
      });
    }
  };


  const onDownloadTemplate = () => {
    const templateData = [
      ['Name', 'Offer link', 'Redirect link', 'Entry domain', 'Traffic source'],
    ];

    const csvContent = templateData.map((row) => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'offerTemplate.csv';

    link.click();

    URL.revokeObjectURL(link.href);
  };

  const onImportOffer = async (data) => {
    if (data.length <= 1) {
      window.alert('No data rows found in CSV. Import canceled.');
      return;
    }

    const headers = data[0];
    const requiredHeaders = [
      'Name',
      'Offer link',
      'Redirect link',
      'Entry domain',
      'Traffic source',
    ];

    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.includes(header)
    );
    if (missingHeaders.length > 0) {
      window.alert(
        `Missing required header(s): ${missingHeaders.join(', ')}. Import canceled.`
      );
      return;
    }

    const formattedData = [];

    const headerIndexMap = requiredHeaders.reduce((acc, header) => {
      acc[header] = headers.indexOf(header);
      return acc;
    }, {});

    for (let i = 1; i < data.length; i++) {
      const row = data[i];

      if (!row || row.length <= 1 || row.every((cell) => !cell)) {
        continue;
      }

      const nameCell = row[headerIndexMap['Name']];
      const offerLinkCell = row[headerIndexMap['Offer link']];
      const redirectLinkCell = row[headerIndexMap['Redirect link']];
      const entryDomainCell = row[headerIndexMap['Entry domain']];
      const trafficSourceCell = row[headerIndexMap['Traffic source']];

      if (
        !nameCell ||
        !offerLinkCell ||
        !redirectLinkCell ||
        !entryDomainCell ||
        !trafficSourceCell
      ) {
        window.alert(`Row ${i + 1} has missing data. Import canceled.`);
        return;
      }

      const redirectDomainId = getRedirectDomainId(redirectLinkCell);
      const entryDomainId = getEntryDomainId(entryDomainCell);
      const trafficSourceId = getTrafficSourceId(trafficSourceCell);

      if (!redirectDomainId) {
        window.alert(
          `Row ${i + 1} - Redirect domain does not exist for "${redirectLinkCell}". Import canceled.`
        );
        return;
      }
      if (!entryDomainId) {
        window.alert(
          `Row ${i + 1} - Entry domain does not exist for "${entryDomainCell}". Import canceled.`
        );
        return;
      }
      if (!trafficSourceId) {
        window.alert(
          `Row ${i + 1} - Traffic source does not exist for "${trafficSourceCell}". Import canceled.`
        );
        return;
      }

      formattedData.push({
        name: nameCell,
        offerLink: offerLinkCell,
        redirectDomainId,
        entryDomainId,
        trafficSourceId,
      });
    }

    if (formattedData.length === 0) {
      window.alert('No valid rows found to import. Import canceled.');
      return;
    }


    try {
      await importOffer().call(formattedData);
      onImportOfferDone();
      window.alert('CSV data successfully processed.');
    } catch (error) {
      console.error('Error importing offer:', error);
      window.alert('An error occurred while importing. Please check the console.');
    }
  };


  const getRedirectLink = (offer) => {
    const { redirectDomainId = '' } = offer

    const domainData = domainMap[redirectDomainId];

    if (!domainData) {
      return ''
    }

    const domainName = domainData.domainName ?? ''

    const isOnlyDrops = domainName.includes('onlydrops')

    try {
      const domainObject = new URL(domainData.domainLink);

      if (isOnlyDrops) {
        domainObject.searchParams.set('uid', offer.uniqueId)
        return domainObject.toString()
      }

      const url = `${domainObject.origin}/r/${offer.uniqueId}`

      return url

    } catch (error) {
      console.error(error)
      console.log(offer.name);
      return ''
    }
  }

  const getEntryDomainLink = (offer) => {
    const { entryDomainId = '' } = offer

    const entryDomainData = domainMap[entryDomainId];

    if (!entryDomainData) return ''

    try {
      const url = new URL(entryDomainData.domainLink)

      const fullUrl = `${url.origin}/offer/${offer.uniqueId}`

      return fullUrl
    } catch (error) {
      return ''
    }
  }



  const onExportAll = async () => {
    try {
      const response = await getOffers().call(1, totalRowCount || 99999);
      const offers = response.offers || response;

      const formatOfferData = (offers) => {
        offers.forEach((offer, index) => {
          const redirectLink = getRedirectLink(offer)
          offers[index].redirectLink = redirectLink;

          const entryDomainLink = getEntryDomainLink(offer)
          offers[index].entryDomain = entryDomainLink;

          const trafficSourceId = offer.trafficSourceId || '';
          const trafficSourceData = trafficMap[trafficSourceId];
          offers[index].trafficSource = trafficSourceData ? trafficSourceData.name : '';

          const redirectDomainData = domainMap[offer.redirectDomainId];
          offers[index].redirectDomainName = redirectDomainData ? (redirectDomainData.domainName || '') : '';

          const entryDomainData = domainMap[offer.entryDomainId];
          offers[index].entryDomainName = entryDomainData ? (entryDomainData.domainName || '') : '';
        });

        return offers;
      };

      const formattedOffers = formatOfferData(offers);

      return formattedOffers;
    } catch (error) {
      console.error('Error exporting all offers:', error);
      window.alert('Failed to export all offers.');
    }
  };


  const modalButtons = useMemo(() => {
    const res = [
      {
        name: 'add',
        label: 'Add',
        renderIf: true,
        icon: <AddIcon />,
        render: (open, handleClose) => {
          return (
            <AddOffer
              open={open}
              handleClose={handleClose}
              onSubmit={onAddOffer}
            />
          );
        },
      },
      {
        name: 'edit',
        label: 'Edit',
        renderIf: Boolean(selectedRow),
        icon: <EditIcon />,
        render: (open, handleClose) => {
          return (
            <EditOffer
              open={open}
              handleClose={handleClose}
              onSubmit={onEditOffer}
              initialData={selectedRow}
            />
          );
        },
      },
      {
        name: 'bulkEdit',
        label: 'Bulk Edit Offers',
        renderIf: Boolean(multipleDataSelected),
        icon: <EditIcon />,
        render: (open, handleClose) => {
          return (
            <BulkEditOffer
              open={open}
              handleClose={handleClose}
              filteredOffers={filteredOffers}
              onEditBulkOffer={onEditBulkOffer}
            />
          );
        },
      },
      {
        name: 'test',
        label: 'Test',
        renderIf: true,
        icon: <BuildIcon />,
        render: (open, handleClose) => {
          return <TestOffer open={open} handleClose={handleClose} />;
        },
      },
      {
        name: 'delete',
        label: 'Delete',
        renderIf: Boolean(rowToDelete.length > 0),
        icon: <DeleteIcon />,
        render: (open, handleClose) => {
          return (
            <DeleteOffer
              open={open}
              handleClose={handleClose}
              onSubmit={onDeleteOffer}
              initialData={rowToDelete}
            />
          );
        },
      },
      {
        name: 'archive',
        label: 'Bulk Archive/Unarchive',
        renderIf: rowToArchive.length > 1,
        icon: <ArchiveIcon />,
        render: (open, handleClose) => {
          return (
            <ArchiveOffer
              open={open}
              handleClose={handleClose}
              onSubmit={handleBulkArchive}
              initialData={rowToArchive}
              showArchive={showArchive}
            />
          );
        },
      },
      {
        name: 'cloneOffer',
        label: 'Clone Offer',
        renderIf: Boolean(selectedRow),
        icon: <FileCopyIcon />,
        render: (open, handleClose) => {
          return (
            <CloneOffer
              open={open}
              handleClose={handleClose}
              onSubmit={handleCloneOffer}
              selectedRow={selectedRow}
            />
          );
        },
      },
      {
        name: 'status',
        label: `${allSelectedStatus ? 'Pause' : 'Unpause'} ${rowToArchive.length > 1 ? 'Offers' : 'Offer'}`,
        renderIf: rowToArchive.length > 0,
        icon: allSelectedStatus ? <PauseIcon /> : <PlayArrowIcon />,
        onClick: handleBulkStatus,
      },
    ];

    return res;
  }, [selectedRow, rowToDelete, rowToArchive, currentData, allSelectedStatus]);

  const exportOptions = {
    name: { label: 'Name' },
    offerLink: { label: 'Offer link' },
    uniqueId: { label: 'Offer unique ID' },
    fallBackUrl: { label: 'Fallback URL' },
    redirectDomainName: { label: 'Redirect domain' },
    redirectLink: { label: 'Redirect link' },
    entryDomainName: { label: 'Entry domain' },
    entryDomain: { label: 'Entry domain link' },
    trafficSource: { label: 'Traffic source' },
    archive: { label: 'Archive status' },
    status: { label: 'Offer status' },
    createdAt: { label: 'Created At', formatter: (val) => val ? new Date(val).toISOString() : '' },
    updatedAt: { label: 'Updated At', formatter: (val) => val ? new Date(val).toISOString() : '' },
  };

  return (
    <DynamicToolbar
      modalButtons={modalButtons}
      allowExport
      allowImport
      exportFileName="offerTable.csv"
      rawExportData={currentData}
      exportOptions={exportOptions}
      onImportOffer={onImportOffer}
      onDownloadTemplate={onDownloadTemplate}
      allowExportAll={true}
      onExportAll={onExportAll}
      allowImportIronDome={true}
      ironDomeImportFunction={ironDomeBulkImport().call}
      setLoading={setLoading}
      onImportIronDomeDone={fetchData}
    />
  );
}

export default memo(OfferToolbar);
